import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import OverflowPopover from './OverflowPopover';

const renderPopover = (props = {}) =>
  render(
    <ConfigProvider>
      <OverflowPopover
        ariaLabel="Show all accounts (2 more)"
        summary={<span>2</span>}
        {...props}
      >
        <span>Hidden content</span>
      </OverflowPopover>
    </ConfigProvider>,
  );

const trigger = () =>
  screen.getByRole('button', { name: /show all accounts/i });

describe('management/shared OverflowPopover', () => {
  it('renders the summary inside the trigger', () => {
    renderPopover();

    expect(trigger()).toHaveTextContent('2');
  });

  // A real button, because Popover only adds aria-haspopup to whatever element
  // it is handed — it does not make it focusable or announce it as a control.
  it('exposes the trigger as a button', () => {
    renderPopover();

    expect(trigger()).toHaveAttribute('type', 'button');
  });

  it('starts closed, hiding its content', () => {
    renderPopover();

    expect(trigger()).toHaveAttribute('aria-expanded', 'false');
    expect(screen.queryByText('Hidden content')).not.toBeInTheDocument();
  });

  it('reveals the content when clicked', async () => {
    renderPopover();

    fireEvent.click(trigger());

    await waitFor(() =>
      expect(screen.getByText('Hidden content')).toBeInTheDocument(),
    );
  });

  it('reports its expanded state', async () => {
    renderPopover();

    fireEvent.click(trigger());

    await waitFor(() =>
      expect(trigger()).toHaveAttribute('aria-expanded', 'true'),
    );
  });

  it('closes again on a second click', async () => {
    renderPopover();

    fireEvent.click(trigger());
    await waitFor(() =>
      expect(screen.getByText('Hidden content')).toBeInTheDocument(),
    );

    fireEvent.click(trigger());

    await waitFor(() =>
      expect(screen.queryByText('Hidden content')).not.toBeInTheDocument(),
    );
  });

  it('closes when the user clicks away', async () => {
    renderPopover();

    fireEvent.click(trigger());
    await waitFor(() =>
      expect(screen.getByText('Hidden content')).toBeInTheDocument(),
    );

    fireEvent.mouseDown(document.body);
    fireEvent.click(document.body);

    await waitFor(() =>
      expect(screen.queryByText('Hidden content')).not.toBeInTheDocument(),
    );
  });

  // A user on hundreds of accounts would otherwise get a panel taller than the
  // viewport, with the overflow unreachable.
  it('scrolls its content instead of growing past the viewport', async () => {
    renderPopover();

    fireEvent.click(trigger());
    await waitFor(() =>
      expect(screen.getByText('Hidden content')).toBeInTheDocument(),
    );

    const panel = screen.getByText('Hidden content').parentElement;

    // Both halves matter, and the cap is the half that fixes the bug: a panel
    // with no cap never overflows, so `overflow-y` alone would pass on a panel
    // still running off the screen.
    // No space after the comma: styled-components minifies the declaration.
    expect(panel).toHaveStyleRule('max-height', 'min(60vh,25rem)');
    expect(panel).toHaveStyleRule('overflow-y', 'auto');
  });

  // The design's pill: the number and the chevron in one outline Tag rather than a
  // tag with a chevron beside it. KeyDS draws the action only when text, action and
  // actionIcon are all given, so the chevron would vanish silently if one were lost.
  describe('the count trigger', () => {
    const renderCount = () => renderPopover({ count: 3, summary: undefined });

    it('shows the count and the chevron in the one control', () => {
      renderCount();

      expect(trigger()).toHaveTextContent('3');
      expect(trigger().querySelector('svg')).toBeInTheDocument();
    });

    it('reveals the content when clicked', async () => {
      renderCount();

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(screen.getByText('Hidden content')).toBeInTheDocument(),
      );
    });

    // KeyDS makes the Tag focusable but binds no key handler, so without one of ours
    // a keyboard user could reach the pill and never open it.
    it.each(['Enter', ' '])('opens on %s from the keyboard', async (key) => {
      renderCount();

      fireEvent.keyDown(trigger(), { key });

      await waitFor(() =>
        expect(screen.getByText('Hidden content')).toBeInTheDocument(),
      );
    });

    it('reports its expanded state', async () => {
      renderCount();

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(trigger()).toHaveAttribute('aria-expanded', 'true'),
      );
    });
  });

  // The chevron tracks the state, which is why visibility is controlled rather
  // than left to Popover.
  it('flips the chevron when opened', async () => {
    const { container } = renderPopover();
    const closedIcon = container.querySelector('svg').outerHTML;

    fireEvent.click(trigger());
    await waitFor(() =>
      expect(screen.getByText('Hidden content')).toBeInTheDocument(),
    );

    expect(container.querySelector('svg').outerHTML).not.toEqual(closedIcon);
  });
});
